import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Spinner, toast } from "@/components/shared";
import { BrandingConfig } from "@/types";
import { onSnapshot, doc } from "firebase/firestore";
import { db } from "@/lib/firebase";

export default function Login() {
  const { signInWithGoogle, signInWithEmail, sendPasswordReset, firebaseUser, loading } = useAuth();
  const navigate = useNavigate();

  // Mobile Google SSO uses signInWithRedirect, which navigates the page
  // away to Google and then back to /login when complete. Without this
  // effect, the user lands back on the login screen still signed-in,
  // creating an apparent "auth loop." Bounce them to / once auth resolves.
  useEffect(() => {
    if (!loading && firebaseUser) navigate("/", { replace: true });
  }, [loading, firebaseUser, navigate]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [branding, setBranding] = useState<BrandingConfig>({ primaryColor: "#0071e3", portalName: "HelpDesk" });
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", "branding"), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as Partial<BrandingConfig>;
      setBranding((b) => ({
        primaryColor: data.primaryColor || b.primaryColor,
        portalName: data.portalName || b.portalName,
        logoUrl: data.logoUrl,
      }));
    });
    return unsub;
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--blue", branding.primaryColor);
    document.title = branding.portalName;
  }, [branding.primaryColor, branding.portalName]);

  const handleGoogle = async () => {
    setGoogleLoading(true); setError("");
    try { await signInWithGoogle(); navigate("/"); }
    catch (err) {
      const code = (err as { code?: string }).code;
      setError(code === "auth/popup-closed-by-user" ? "Sign-in window closed. Try again." : "Google sign-in failed.");
    }
    setGoogleLoading(false);
  };

  const handleEmail = async (e: FormEvent) => {
    e.preventDefault();
    setEmailLoading(true); setError("");
    try { await signInWithEmail(email.trim(), password); navigate("/"); }
    catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found"
          ? "Email or password is incorrect."
          : code === "auth/user-disabled"
          ? "This account is disabled. Contact your admin."
          : "Sign-in failed. Try again."
      );
    }
    setEmailLoading(false);
  };

  const handleReset = async () => {
    if (!email.trim()) { setError("Enter your email above first."); return; }
    try {
      await sendPasswordReset(email.trim());
      toast(`Password reset sent to ${email.trim()}`);
      setShowReset(false);
    } catch {
      toast("Could not send reset email", "error");
    }
  };

  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <div style={{ width: 380 }} className="fade-in">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.portalName} style={{ maxHeight: 56, maxWidth: 220, objectFit: "contain", marginBottom: 12 }} />
          ) : (
            <div style={{ width: 52, height: 52, background: "var(--blue)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>
          )}
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -.5, marginBottom: 4 }}>{branding.portalName}</h1>
          <p style={{ fontSize: 13, color: "var(--text2)" }}>AI-powered IT support</p>
        </div>

        <div className="card card-body" style={{ padding: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, textAlign: "center" }}>Sign in</h2>

          <form onSubmit={handleEmail} style={{ marginTop: 18 }}>
            <div className="fg">
              <label>Email</label>
              <input className="fi" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div className="fg">
              <label>Password</label>
              <input className="fi" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 4 }} disabled={emailLoading}>
              {emailLoading ? <Spinner size={16} color="white" /> : "Sign in"}
            </button>
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <button type="button" onClick={() => setShowReset(!showReset)}
                style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 12, cursor: "pointer", padding: 0 }}>
                Forgot password?
              </button>
            </div>
            {showReset && (
              <div style={{ marginTop: 8, padding: 10, background: "var(--gray)", borderRadius: 6, fontSize: 12 }}>
                Enter your email above and click to receive a reset link.
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={handleReset}>
                  Send reset email
                </button>
              </div>
            )}
          </form>

          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0", color: "var(--text3)", fontSize: 11, fontWeight: 600, letterSpacing: .5, textTransform: "uppercase" }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            or
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <button onClick={handleGoogle} disabled={googleLoading}
            style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              padding: "11px 20px", borderRadius: "var(--rs)", border: "1px solid var(--border-med)",
              // Background is hardcoded white to stay within Google's sign-in
              // branding, so the text colour has to be pinned too — inheriting
              // the theme's --text renders white-on-white in dark mode.
              background: "white", color: "#3c4043",
              fontSize: 14, fontWeight: 500, fontFamily: "var(--font)",
              cursor: googleLoading ? "default" : "pointer",
            }}>
            {googleLoading ? <Spinner size={18} color="var(--blue)" /> : (
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            )}
            {googleLoading ? "Signing in…" : "Sign in with Google"}
          </button>

          {error && (
            <div style={{ marginTop: 14, padding: "10px 12px", background: "var(--red-l)", border: "1px solid rgba(255,59,48,.2)", borderRadius: "var(--rs)", fontSize: 12, color: "var(--red-t)", textAlign: "center" }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
